import React from 'react';
import classNames from 'classnames';
import { useTranslation } from '@wix/yoshi-flow-editor';
import type { Member } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-feed-page/types';
import type { CurrentUser } from '@app/external/common/store/auth/types';
import FluidAvatarImage from '../fluid-avatar-image';
import styles from './user-avatar-image.scss';

const getImageSize = (styleVariable: string) => parseInt(styleVariable, 10) * 2;

const IMAGE_SIZE_MAP = {
  large: getImageSize(styles.IMAGE_SIZE_LARGE),
  extraLarge: getImageSize(styles.IMAGE_SIZE_EXTRA_LARGE),
  promote: getImageSize(styles.IMAGE_SIZE_PROMOTE),
  widget: getImageSize(styles.IMAGE_SIZE_WIDGET),
  comment: getImageSize(styles.IMAGE_SIZE_COMMENT),
  commentSearch: getImageSize(styles.IMAGE_SIZE_COMMENT_SEARCH),
};
const IMAGE_SIZE_DEFAULT = getImageSize(styles.IMAGE_SIZE_DEFAULT);

type Props = {
  user: CurrentUser | Member | null;
  type?: keyof typeof IMAGE_SIZE_MAP;
  className?: string;
};

const isMemberAreaMember = (user: CurrentUser | Member): user is CurrentUser => {
  if ('profile' in user) {
    return user?.profile != null;
  }
  return false;
};

const getUserInfo = (user: CurrentUser | Member) => {
  if (isMemberAreaMember(user)) {
    return {
      name: user.profile?.nickname,
      image: user.profile?.photo,
    };
  }

  return {
    name: user.name,
    image: user.image?.url ? user.image : undefined,
  };
};

const UserAvatarImage: React.FC<Props> = ({ className, type, user }) => {
  const imageSize = (type && IMAGE_SIZE_MAP[type]) ?? IMAGE_SIZE_DEFAULT;
  const [t] = useTranslation();

  if (!user) {
    return null;
  }

  const { name, image } = getUserInfo(user);

  return (
    <span className={classNames(styles.image, type && styles[type], className, 'avatar-image')}>
      <FluidAvatarImage
        ariaLabel={`${t('aria-label.authors-picture')}: ${name}`}
        image={image}
        size={imageSize}
      />
    </span>
  );
};

export default UserAvatarImage;
